import { apiClient, ApiResponse } from "./client";

// ============================================================================
// Types
// ============================================================================

export interface RegisteredDevice {
    id: string;
    organizationId: number;
    deviceName: string;
    deviceToken: string;
    deviceInfo: Record<string, unknown>;
    isActive: boolean;
    registeredBy: number;
    lastAccessedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface DeviceRegistrationCode {
    id: string;
    code: string;
    deviceName: string;
    expiresAt: string;
    isUsed: boolean;
    isExpired: boolean;
    createdAt: string;
}

export interface DeviceVerifyResponse {
    verified: boolean;
    deviceName: string | null;
}

export interface DeviceActivateResponse {
    deviceToken: string;
    deviceName: string;
}

// ============================================================================
// API
// ============================================================================

export const deviceApi = {
    // Admin APIs
    getAll: () =>
        apiClient.get<ApiResponse<RegisteredDevice[]>>("/b2b/devices"),

    register: (data: { deviceName: string; deviceInfo?: Record<string, unknown> }) =>
        apiClient.post<ApiResponse<RegisteredDevice>>("/b2b/devices/register", data),

    update: (deviceId: string, data: { deviceName?: string; isActive?: boolean }) =>
        apiClient.patch<ApiResponse<RegisteredDevice>>(`/b2b/devices/${deviceId}`, data),

    delete: (deviceId: string) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/devices/${deviceId}`),

    // Registration Code APIs
    createCode: () =>
        apiClient.post<ApiResponse<DeviceRegistrationCode>>("/b2b/devices/codes"),

    getCodes: () =>
        apiClient.get<ApiResponse<DeviceRegistrationCode[]>>("/b2b/devices/codes"),

    // Verification & Activation
    verify: (deviceToken: string) =>
        apiClient.post<ApiResponse<DeviceVerifyResponse>>("/b2b/devices/verify", { deviceToken }),

    activate: (code: string, deviceInfo?: Record<string, unknown>) =>
        apiClient.post<ApiResponse<DeviceActivateResponse>>("/b2b/devices/activate", { code, deviceInfo }),
};

// ============================================================================
// Device Token Helpers
// ============================================================================

const DEVICE_TOKEN_KEY = "quebon-device-token";

export function getStoredDeviceToken(): string | null {
    return localStorage.getItem(DEVICE_TOKEN_KEY);
}

export function setStoredDeviceToken(token: string): void {
    localStorage.setItem(DEVICE_TOKEN_KEY, token);
}

export function removeStoredDeviceToken(): void {
    localStorage.removeItem(DEVICE_TOKEN_KEY);
}

export function getDeviceInfo(): Record<string, unknown> {
    return {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        language: navigator.language,
    };
}
